import { useState } from 'react'
import { BackButton } from '../components/BackButton'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api'
import { useToast } from '../components/Toasts'
import { PasswordOnce } from '../components/PasswordOnce'
import { emailedNotice } from '../lib/admin-view'
import { checkSlug, slugFrom, tidyDomain, checkDomain } from '../lib/companyRules'
import { splitCommas } from '../lib/format'

/**
 * Setting a company up, and its first admin with it.
 *
 * One screen, not two. A company with nobody in it cannot be signed in to, so
 * finishing here means the person on the other end can start today rather than
 * waiting for somebody to remember the second half.
 */
export function AddCompany() {
  const navigate = useNavigate()
  const toast = useToast()

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  // Whether the short name is still following the company name. It stops the
  // moment she types one herself, so her typing is never overwritten.
  const [slugTouched, setSlugTouched] = useState(false)
  const [domains, setDomains] = useState('')
  const [adminName, setAdminName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // `password` is null through Auth0: the first admin was emailed a link instead.
  const [made, setMade] = useState<{ slug: string; email: string; password: string | null } | null>(null)

  // What she typed, not a tidied version. A capital is refused with a reason
  // rather than silently changed, because this is the one field that can never
  // be changed afterwards. The suggestion from the company name is already
  // lower case, so nobody meets the rule by accident.
  const short = (slugTouched ? slug : slugFrom(name)).trim()
  const domainList = splitCommas(domains).map(tidyDomain).filter(Boolean)

  const slugProblem = short ? checkSlug(short) : null
  const domainProblem = domainList.map(checkDomain).find(Boolean) ?? null
  const emailProblem =
    adminEmail && !adminEmail.includes('@') ? 'That does not look like an email address.' : null

  const ready =
    name.trim().length > 0 && short.length > 0 && !slugProblem && !domainProblem
    && adminEmail.trim().length > 0 && !emailProblem

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!ready) return
    setBusy(true)
    setError('')
    try {
      const out = await api.createCompany({
        name: name.trim(),
        slug: short,
        domains: domainList,
        adminEmail: adminEmail.trim().toLowerCase(),
        adminName: adminName.trim(),
      })
      toast.ok(`${out.company.name} is set up.`)
      setMade({ slug: out.company.slug, email: out.person.email, password: out.password ?? null })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That company could not be created.')
      setBusy(false)
    }
  }

  // The password is on screen and nowhere else. Leaving before it is copied
  // means setting a new one, so the form is replaced rather than sitting
  // behind it inviting another press.
  if (made) {
    return (
      <section className="screen screen--narrow">
        <header className="title-row">
          <div>
            <h1 className="page">{name.trim()} is set up</h1>
            <p className="subline">{made.password !== null ? 'One thing left: send the admin their password.' : 'The first admin has been emailed a link to set their password.'}</p>
          </div>
        </header>
        {made.password !== null ? (
          <PasswordOnce
            email={made.email}
            password={made.password}
            heading="The first admin's password"
            onDone={() => navigate(`/companies/${encodeURIComponent(made.slug)}`)}
          />
        ) : (
          <div className="banner banner--ok" role="status">
            <span>{emailedNotice(made.email)}</span>
            <button type="button" className="btn btn--sm" onClick={() => navigate(`/companies/${encodeURIComponent(made.slug)}`)}>Open the company</button>
          </div>
        )}
      </section>
    )
  }

  return (
    <section className="screen screen--narrow">
      {/* The way out, in the corner, like every other screen reached from another. */}
      <BackButton to="companies" onClick={() => navigate('/companies')} />
      <header className="title-row">
        <div>
          <h1 className="page">Add a company</h1>
          <p className="subline">The company, who signs in to it, and its first admin.</p>
        </div>
        <div className="actions">
          <Link className="btn" to="/companies">Cancel</Link>
        </div>
      </header>

      <form onSubmit={submit} noValidate className="card card--pad form">
        <label className="field">
          <span>Company name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Northwind Autonomy"
            autoFocus
            required
          />
          <span className="hint">What people see on their screen.</span>
        </label>

        <label className="field">
          <span>Short name</span>
          <input
            value={short}
            onChange={(e) => {
              setSlugTouched(true)
              setSlug(e.target.value)
            }}
            required
          />
          {/*
            This is the one field on the screen that cannot be changed later.
            It is part of every login and part of how every record the company
            will ever own is filed, so saying so now costs a line and saves a
            migration.
          */}
          <span className={slugProblem ? 'hint hint--bad' : 'hint'}>
            {slugProblem ?? 'Used in every login and cannot be changed later. Lower case letters, numbers and hyphens.'}
          </span>
        </label>

        <label className="field">
          <span>Sign-in domains</span>
          <input
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            placeholder="northwind.com, northwind.co.uk"
          />
          <span className={domainProblem ? 'hint hint--bad' : 'hint'}>
            {domainProblem
              ?? 'Separate several with commas. Someone typing only their email is sent to this company. Can be changed later.'}
          </span>
        </label>

        <hr className="rule" />

        <p className="form__note">
          The first admin. They can add everybody else themselves once they are in.
        </p>

        <label className="field">
          <span>Their name</span>
          <input value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Sarah Reyes" />
        </label>

        <label className="field">
          <span>Their email address</span>
          <input
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            placeholder="sarah@northwind.com"
            required
          />
          <span className={emailProblem ? 'hint hint--bad' : 'hint'}>
            {emailProblem ?? 'They sign in with this. A password is made for them on the next screen.'}
          </span>
        </label>

        {error && (
          <div className="banner banner--bad" role="alert">
            <span aria-hidden="true">!</span>
            {error}
          </div>
        )}

        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={!ready || busy}>
            {busy ? 'Setting up…' : 'Create the company'}
          </button>
          <Link className="btn" to="/companies">Cancel</Link>
        </div>
      </form>
    </section>
  )
}
